/**
 * NotificationBell — 通知铃铛组件
 * 显示未读数量 + 下拉通知列表 + 一键全部已读
 *
 * **DSL-driven**: Poll interval comes from LayoutConfig.notificationPollMs.
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { formatRelativeTime } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Bell, Check, CheckCheck } from 'lucide-react';
import { useLayoutConfigContext } from '@/lib/use-layout-config';
import { useNetworkNotificationBadge } from '@/hooks/useBooxSocket';

interface Notification {
  id: string;
  title: string;
  body: string | null;
  priority: string;
  entity_type: string | null;
  entity_id: string | null;
  action_url: string | null;
  read_at: string | null;
  created_at: string;
}

export function NotificationBell() {
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<Notification[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { config } = useLayoutConfigContext();
  const pollMs = config.notificationPollMs;

  // Real-time network notification count from WebSocket
  const { unreadCount: networkUnread, clear: clearNetworkBadge } = useNetworkNotificationBadge();

  // Total badge = tenant notifications + WebSocket-delivered network notifications
  const totalUnread = unread + networkUnread;

  // Fetch unread count periodically
  const fetchCount = useCallback(async () => {
    try {
      const res = await api.get<{ count: number }>('/notifications/unread');
      setUnread(res.count);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    void fetchCount();
    const timer = setInterval(fetchCount, pollMs); // DSL-driven poll interval
    return () => clearInterval(timer);
  }, [fetchCount, pollMs]);

  // Click outside to close
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const loadItems = useCallback(async () => {
    setLoadingItems(true);
    try {
      const res = await api.get<{ items: Notification[] }>('/notifications?limit=10');
      setItems(res.items ?? []);
    } catch {
      setItems([]);
    } finally {
      setLoadingItems(false);
    }
  }, []);

  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    if (next) {
      void loadItems();
      clearNetworkBadge(); // clear WebSocket-delivered network badge on open
    }
  };

  const handleMarkRead = async (id: string) => {
    try {
      await api.patch<unknown>(`/notifications/${id}/read`, {});
      setItems(prev => prev.map(n => n.id === id ? { ...n, read_at: new Date().toISOString() } : n));
      setUnread(prev => Math.max(0, prev - 1));
    } catch {
      // ignore
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.post<unknown>('/notifications/read-all', {});
      setItems(prev => prev.map(n => ({ ...n, read_at: n.read_at ?? new Date().toISOString() })));
      setUnread(0);
    } catch {
      // ignore
    }
  };

  const priorityColor: Record<string, string> = {
    urgent: 'text-destructive',
    high: 'text-warning',
    normal: 'text-foreground',
    low: 'text-muted-foreground',
  };

  return (
    <div className="relative" ref={ref}>
      <Button variant="ghost" size="icon" className="relative h-8 w-8" onClick={handleToggle}>
        <Bell className="h-4 w-4" />
        {totalUnread > 0 && (
          <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-[10px] font-bold text-destructive-foreground">
            {totalUnread > 9 ? '9+' : totalUnread}
          </span>
        )}
      </Button>

      {open && (
        <div className="absolute right-0 top-10 z-50 w-80 rounded-md border bg-popover text-popover-foreground shadow-md">
          {/* Header */}
          <div className="flex items-center justify-between border-b px-3 py-2">
            <span className="text-sm font-medium">{t('notification.title')}</span>
            {totalUnread > 0 && (
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={handleMarkAllRead}>
                <CheckCheck className="mr-1 h-3 w-3" />
                {t('notification.markAllRead')}
              </Button>
            )}
          </div>

          {/* List */}
          <div className="max-h-80 overflow-y-auto">
            {loadingItems ? (
              <div className="p-4 text-center text-xs text-muted-foreground">{t('notification.loading')}</div>
            ) : items.length === 0 ? (
              <div className="p-4 text-center text-xs text-muted-foreground">{t('notification.empty')}</div>
            ) : (
              items.map(n => (
                <div
                  key={n.id}
                  className={`flex items-start gap-2 border-b last:border-0 px-3 py-2 ${
                    !n.read_at ? 'bg-muted/50' : ''
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${priorityColor[n.priority] ?? ''}`}>
                      {n.title}
                    </p>
                    {n.body && (
                      <p className="text-xs text-muted-foreground line-clamp-2">{n.body}</p>
                    )}
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      {formatRelativeTime(n.created_at, t)}
                    </p>
                  </div>
                  {!n.read_at && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 shrink-0"
                      onClick={() => handleMarkRead(n.id)}
                    >
                      <Check className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
